/**
 * SearchAiz Data Source Registry
 *
 * Centralized registry of all searchable data sources.
 * Each source maps to specific Qdrant collections and has:
 * - Access control (permissions, billing tier)
 * - UI configuration (icons, colors)
 * - Search configuration (patterns, AI support)
 */

import {
  BookOpen,
  Package,
  Building2,
  HeadphonesIcon,
  Phone,
  Users,
  Scale,
  Zap,
  Layout,
  BarChart3,
  Globe,
  FileText,
  type LucideIcon,
} from 'lucide-react';

import type {
  DataSourceDefinition,
  DataSourceCategory,
  SearchPattern,
  BillingTier,
} from '../types';

// =============================================================================
// DATA SOURCE REGISTRY
// =============================================================================

/**
 * Complete registry of all available data sources
 */
export const DATA_SOURCE_REGISTRY: DataSourceDefinition[] = [
  // -------------------------------------------------------------------------
  // Knowledge Base
  // -------------------------------------------------------------------------
  {
    id: 'knowledge_base',
    name: 'Knowledge Base',
    description: 'Documents, policies, and procedures',
    category: 'knowledge_base',
    collections: ['repositories', 'repository_chunks'],
    requiredPermissions: ['search:knowledge_base'],
    icon: BookOpen,
    iconColor: 'text-purple-500',
    badgeColor: 'bg-purple-900/30 text-purple-400',
    defaultK: 10,
    supportedPatterns: ['unified', 'documents', 'repositories', 'cascade', 'aggregate'],
    supportsReranking: true,
    supportsAiAnalysis: true,
  },

  // -------------------------------------------------------------------------
  // Product Data
  // -------------------------------------------------------------------------
  {
    id: 'product_data',
    name: 'Products',
    description: 'Product catalog, specifications, and files',
    category: 'product_data',
    collections: ['product_catalog', 'product_specs', 'product_files'],
    requiredPermissions: ['search:products'],
    icon: Package,
    iconColor: 'text-emerald-500',
    badgeColor: 'bg-emerald-900/30 text-emerald-400',
    defaultK: 15,
    supportedPatterns: ['unified', 'documents'],
    supportsReranking: true,
    supportsAiAnalysis: false,
  },

  // -------------------------------------------------------------------------
  // Business Data (CRM)
  // -------------------------------------------------------------------------
  {
    id: 'business_data',
    name: 'CRM',
    description: 'Customers, contacts, and invoices',
    category: 'business_data',
    collections: ['customers', 'contacts', 'invoices'],
    requiredPermissions: ['search:crm'],
    icon: Building2,
    iconColor: 'text-blue-500',
    badgeColor: 'bg-blue-900/30 text-blue-400',
    defaultK: 10,
    supportedPatterns: ['unified'],
    supportsReranking: true,
    supportsAiAnalysis: false,
  },

  // -------------------------------------------------------------------------
  // Support Data
  // -------------------------------------------------------------------------
  {
    id: 'support_data',
    name: 'Support History',
    description: 'Orders, tickets, and lessons learned',
    category: 'support_data',
    collections: ['orders_history', 'tickets_history', 'tenant_lessons'],
    requiredPermissions: ['search:support'],
    icon: HeadphonesIcon,
    iconColor: 'text-amber-500',
    badgeColor: 'bg-amber-900/30 text-amber-400',
    defaultK: 10,
    supportedPatterns: ['unified'],
    supportsReranking: true,
    supportsAiAnalysis: true,
  },

  // -------------------------------------------------------------------------
  // Call History (Supabase-backed)
  // -------------------------------------------------------------------------
  {
    id: 'calls',
    name: 'Calls',
    description: 'Completed call history — case numbers, callers, summaries',
    category: 'calls',
    collections: ['calls'],
    requiredPermissions: ['search:calls'],
    icon: Phone,
    iconColor: 'text-amber-500',
    badgeColor: 'bg-amber-900/30 text-amber-400',
    defaultK: 10,
    supportedPatterns: ['unified'],
    supportsReranking: false,
    supportsAiAnalysis: false,
  },

  // -------------------------------------------------------------------------
  // Entities (Supabase-backed)
  // -------------------------------------------------------------------------
  {
    id: 'entities',
    name: 'Entities',
    description: 'People, locations, organizations, and objects from calls',
    category: 'entities',
    collections: ['entities'],
    requiredPermissions: ['search:entities'],
    icon: Users,
    iconColor: 'text-violet-500',
    badgeColor: 'bg-violet-900/30 text-violet-400',
    defaultK: 10,
    supportedPatterns: ['unified'],
    supportsReranking: false,
    supportsAiAnalysis: false,
  },

  // -------------------------------------------------------------------------
  // Web Search (AI-powered internet search)
  // -------------------------------------------------------------------------
  {
    id: 'web',
    name: 'Web',
    description: 'AI-powered internet search',
    category: 'web',
    collections: ['web'],
    requiredPermissions: ['search:web'],
    icon: Globe,
    iconColor: 'text-blue-500',
    badgeColor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    defaultK: 8,
    supportedPatterns: ['unified'],
    supportsReranking: false,
    supportsAiAnalysis: false,
  },

  // -------------------------------------------------------------------------
  // Operations
  // -------------------------------------------------------------------------
  {
    id: 'operations',
    name: 'Operations',
    description: 'Laws, policies, and procedures',
    category: 'operations',
    collections: ['laws_policies', 'service_procedures', 'training_materials', 'incident_patterns'],
    requiredPermissions: ['search:operations'],
    icon: Scale,
    iconColor: 'text-cyan-500',
    badgeColor: 'bg-cyan-900/30 text-cyan-400',
    defaultK: 10,
    supportedPatterns: ['unified', 'documents'],
    supportsReranking: true,
    supportsAiAnalysis: true,
  },

  // -------------------------------------------------------------------------
  // Realtime (Live Call Context)
  // -------------------------------------------------------------------------
  {
    id: 'realtime',
    name: 'Realtime',
    description: 'Live call context and attachments',
    category: 'realtime',
    collections: ['realtime_chunks', 'suggested_actions', 'call_attachments'],
    requiredPermissions: ['search:realtime'],
    icon: Zap,
    iconColor: 'text-rose-500',
    badgeColor: 'bg-rose-900/30 text-rose-400',
    defaultK: 5,
    supportedPatterns: ['unified'],
    supportsReranking: false, // Real-time results shouldn't be delayed
    supportsAiAnalysis: false,
  },

  // -------------------------------------------------------------------------
  // Application (Navigation, Settings)
  // -------------------------------------------------------------------------
  {
    id: 'application',
    name: 'Application',
    description: 'Pages, settings, and navigation',
    category: 'application',
    collections: ['pages', 'settings', 'api_docs', 'help_docs'],
    requiredPermissions: ['search:app'],
    icon: Layout,
    iconColor: 'text-slate-500',
    badgeColor: 'bg-slate-800/50 text-slate-400',
    defaultK: 5,
    supportedPatterns: ['unified'],
    supportsReranking: false,
    supportsAiAnalysis: false,
  },

  // -------------------------------------------------------------------------
  // Analytics (Admin only)
  // -------------------------------------------------------------------------
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Metrics, usage data, and logs',
    category: 'analytics',
    collections: ['llm_metrics', 'user_activity', 'billing_invoices', 'system_events'],
    requiredPermissions: ['search:analytics', 'admin'],
    requiredBillingTier: 'enterprise',
    icon: BarChart3,
    iconColor: 'text-indigo-500',
    badgeColor: 'bg-indigo-900/30 text-indigo-400',
    defaultK: 10,
    supportedPatterns: ['unified'],
    supportsReranking: false,
    supportsAiAnalysis: true,
  },
];

// =============================================================================
// REGISTRY ACCESS FUNCTIONS
// =============================================================================

/**
 * Map of source ID to definition for O(1) lookup
 */
const sourceMap = new Map<string, DataSourceDefinition>(
  DATA_SOURCE_REGISTRY.map((source) => [source.id, source])
);

/**
 * Get a data source by ID
 */
export function getDataSource(id: string): DataSourceDefinition | undefined {
  return sourceMap.get(id);
}

/**
 * Get all data sources
 */
export function getAllDataSources(): DataSourceDefinition[] {
  return DATA_SOURCE_REGISTRY;
}

/**
 * Get data sources by category
 */
export function getDataSourcesByCategory(
  category: DataSourceCategory
): DataSourceDefinition[] {
  return DATA_SOURCE_REGISTRY.filter((source) => source.category === category);
}

/**
 * Get data sources that support a specific search pattern
 */
export function getDataSourcesByPattern(
  pattern: SearchPattern
): DataSourceDefinition[] {
  return DATA_SOURCE_REGISTRY.filter((source) =>
    source.supportedPatterns.includes(pattern)
  );
}

/**
 * Get data sources that support AI reranking
 */
export function getDataSourcesWithReranking(): DataSourceDefinition[] {
  return DATA_SOURCE_REGISTRY.filter((source) => source.supportsReranking);
}

/**
 * Get data sources that support AI analysis
 */
export function getDataSourcesWithAnalysis(): DataSourceDefinition[] {
  return DATA_SOURCE_REGISTRY.filter((source) => source.supportsAiAnalysis);
}

/**
 * Check if a user has access to a data source based on permissions and billing tier
 */
export function hasAccessToSource(
  source: DataSourceDefinition,
  userPermissions: string[],
  billingTier: BillingTier
): boolean {
  // Check permissions
  const hasAllPermissions = source.requiredPermissions.every((perm) =>
    userPermissions.includes(perm)
  );

  if (!hasAllPermissions) {
    return false;
  }

  // Check billing tier if required
  if (source.requiredBillingTier) {
    const tierOrder: BillingTier[] = ['free', 'starter', 'professional', 'enterprise'];
    const requiredIndex = tierOrder.indexOf(source.requiredBillingTier);
    const userIndex = tierOrder.indexOf(billingTier);
    if (userIndex < requiredIndex) {
      return false;
    }
  }

  return true;
}

/**
 * Filter sources to only those the user can access
 */
export function filterAccessibleSources(
  sourceIds: string[],
  userPermissions: string[],
  billingTier: BillingTier
): DataSourceDefinition[] {
  return sourceIds
    .map((id) => getDataSource(id))
    .filter((source): source is DataSourceDefinition =>
      source !== undefined && hasAccessToSource(source, userPermissions, billingTier)
    );
}

/**
 * Get the collections to search for a set of source IDs
 */
export function getCollectionsForSources(sourceIds: string[]): string[] {
  const collections = new Set<string>();
  for (const id of sourceIds) {
    const source = getDataSource(id);
    if (source) {
      for (const collection of source.collections) {
        collections.add(collection);
      }
    }
  }
  return Array.from(collections);
}

/**
 * Determine the best search pattern for a set of sources
 * Returns the most capable pattern that all sources support
 */
export function getBestPatternForSources(sourceIds: string[]): SearchPattern {
  const sources = sourceIds
    .map((id) => getDataSource(id))
    .filter((s): s is DataSourceDefinition => s !== undefined);

  if (sources.length === 0) {
    return 'unified';
  }

  // Find patterns supported by all sources
  const patternPriority: SearchPattern[] = ['cascade', 'aggregate', 'documents', 'repositories', 'unified'];

  for (const pattern of patternPriority) {
    if (sources.every((s) => s.supportedPatterns.includes(pattern))) {
      return pattern;
    }
  }

  // Fallback to unified (should always be supported)
  return 'unified';
}

/**
 * Check if reranking is available for a set of sources
 */
export function canRerankSources(sourceIds: string[]): boolean {
  return sourceIds.every((id) => {
    const source = getDataSource(id);
    return source?.supportsReranking ?? false;
  });
}

/**
 * Check if AI analysis is available for a set of sources
 */
export function canAnalyzeSources(sourceIds: string[]): boolean {
  return sourceIds.some((id) => {
    const source = getDataSource(id);
    return source?.supportsAiAnalysis ?? false;
  });
}

// =============================================================================
// ICON AND STYLE HELPERS
// =============================================================================

/**
 * Get the icon component for a source
 */
export function getSourceIcon(sourceId: string): LucideIcon {
  return getDataSource(sourceId)?.icon ?? FileText;
}

/**
 * Get the icon color class for a source
 */
export function getSourceIconColor(sourceId: string): string {
  return getDataSource(sourceId)?.iconColor ?? 'text-slate-500';
}

/**
 * Get the badge color classes for a source
 */
export function getSourceBadgeColor(sourceId: string): string {
  return getDataSource(sourceId)?.badgeColor ?? 'bg-slate-800/50 text-slate-400';
}

// =============================================================================
// DEFAULT SOURCES BY CONTEXT
// =============================================================================

/**
 * Default sources for different search contexts
 */
export const DEFAULT_SOURCES_BY_CONTEXT: Record<string, string[]> = {
  header: ['calls', 'knowledge_base', 'application'],
  fullPage: ['calls', 'entities', 'knowledge_base', 'web', 'operations', 'application'],
  modal: ['calls', 'knowledge_base', 'web'],
  sidebar: ['knowledge_base'],
  embedded: ['knowledge_base'],
};

/**
 * Get default sources for a context
 */
export function getDefaultSourcesForContext(context: string): string[] {
  return DEFAULT_SOURCES_BY_CONTEXT[context] ?? DEFAULT_SOURCES_BY_CONTEXT.header;
}
